import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
  CircleUser,
  LogOut,
  User,
  Building2,
  SlidersHorizontal,
  Check,
  Search,
} from 'lucide-react';
import { useAuthStore } from 'stores/authStore';
import { useUserProfileStore } from 'stores/userProfileStore';
import { useActiveCompanyStore } from 'stores/activeCompanyStore';
import { useCommandPaletteStore } from 'stores/commandPaletteStore';
import { useCompanyServiceQueries } from 'services/company';
import styles from './Header.module.scss';
import { ROUTES } from 'utils/constants';

function Header() {
  const authLogout = useAuthStore((state) => state.logout);
  const profile = useUserProfileStore((s) => s.profile);
  const profilePictureUrl = useUserProfileStore((s) => s.profilePictureUrl);
  const getProfilePictureDownloadUrl = useUserProfileStore(
    (s) => s.getProfilePictureDownloadUrl,
  );
  const activeCompanyId = useActiveCompanyStore((s) => s.activeCompanyId);
  const availableCompanies = useActiveCompanyStore((s) => s.availableCompanies);
  const setActiveCompanyId = useActiveCompanyStore((s) => s.setActiveCompanyId);
  const companyService = useCompanyServiceQueries();
  const navigate = useNavigate();
  const location = useLocation();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [companyLogoUrls, setCompanyLogoUrls] = useState<
    Record<string, string | null>
  >({});
  const [transactionsDropdownOpen, setTransactionsDropdownOpen] =
    useState(false);
  const [masterDataDropdownOpen, setMasterDataDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const transactionsDropdownRef = useRef<HTMLDivElement>(null);
  const masterDataDropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
      if (
        transactionsDropdownRef.current &&
        !transactionsDropdownRef.current.contains(event.target as Node)
      ) {
        setTransactionsDropdownOpen(false);
      }
      if (
        masterDataDropdownRef.current &&
        !masterDataDropdownRef.current.contains(event.target as Node)
      ) {
        setMasterDataDropdownOpen(false);
      }
    };

    if (isDropdownOpen || transactionsDropdownOpen || masterDataDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen, transactionsDropdownOpen, masterDataDropdownOpen]);

  // Close dropdowns when route changes
  useEffect(() => {
    setTransactionsDropdownOpen(false);
    setMasterDataDropdownOpen(false);
  }, [location.pathname]);

  // Populate cached profile picture URL when profile has a picture
  useEffect(() => {
    if (profile?.profilePicturePath) {
      void getProfilePictureDownloadUrl();
    }
  }, [profile?.profilePicturePath, getProfilePictureDownloadUrl]);

  useEffect(() => {
    if (availableCompanies.length === 0) {
      setCompanyLogoUrls({});
      return;
    }
    let cancelled = false;
    const loadCompanyLogos = async () => {
      const entries = await Promise.all(
        availableCompanies.map(async (company) => {
          if (!company.logoPath) {
            return [company.id, null] as const;
          }
          try {
            const result = await companyService.getCompanyLogoDownloadUrl(
              company.id,
            );
            return [company.id, result.downloadUrl ?? null] as const;
          } catch {
            return [company.id, null] as const;
          }
        }),
      );
      if (!cancelled) {
        setCompanyLogoUrls(Object.fromEntries(entries));
      }
    };
    void loadCompanyLogos();
    return () => {
      cancelled = true;
    };
  }, [availableCompanies, companyService]);

  const handleLogout = async () => {
    setIsDropdownOpen(false);
    await authLogout();
    navigate(ROUTES.HOME, { replace: true });
  };

  const handleSwitchCompany = async (companyId: string) => {
    try {
      await setActiveCompanyId(companyId);
      setIsDropdownOpen(false);
      // Data hooks will automatically detect the activeCompanyId change and reload
      // No need to reload the page - state updates will trigger data refetch
    } catch (error) {
      console.error('Failed to switch company:', error);
    }
  };

  // Get user initials from username
  const getInitials = (username: string | null | undefined): string => {
    if (!username) return '';
    const parts = username.trim().split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return username.substring(0, 2).toUpperCase();
  };

  const openCommandPalette = useCommandPaletteStore((s) => s.open);

  // Profile button should represent the user, not the active company.
  const displayInitials = profile?.username
    ? getInitials(profile.username)
    : '';
  const displayName = profile?.username || '';

  return (
    <>
      <header className={styles.header}>
        <Link to={ROUTES.DASHBOARD} className={styles.appName}>
          Abrasoft Hub
        </Link>
        <nav className={styles.nav}>
          <Link
            to={ROUTES.DASHBOARD}
            className={`${styles.navLink} ${
              location.pathname === ROUTES.DASHBOARD ? styles.active : ''
            }`}
          >
            Dashboard
          </Link>

          <Link to={ROUTES.REPORTS.ROOT} className={styles.navLink}>
            Reports
          </Link>
        </nav>
        <button
          className={styles.searchButton}
          onClick={openCommandPalette}
          aria-label="Search (Ctrl+G)"
          title="Search (Ctrl+G)"
        >
          <Search size={18} />
        </button>
        <div className={styles.profileContainer} ref={dropdownRef}>
          <button
            className={styles.profileButton}
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            aria-label={
              displayName ? `${displayName} profile menu` : 'Profile menu'
            }
            title={displayName || 'Profile'}
          >
            {profilePictureUrl ? (
              <img
                src={profilePictureUrl}
                alt=""
                className={styles.profilePicture}
                aria-hidden
              />
            ) : displayInitials ? (
              <span className={styles.profileInitials} aria-hidden="true">
                {displayInitials}
              </span>
            ) : (
              <CircleUser size={24} />
            )}
          </button>
          {isDropdownOpen && (
            <div className={styles.dropdown}>
              {displayName && (
                <div className={styles.dropdownHeader}>
                  <div className={styles.dropdownUserName}>{displayName}</div>
                  {profile?.email && (
                    <div className={styles.dropdownUserEmail}>
                      {profile.email}
                    </div>
                  )}
                </div>
              )}
              <Link
                to={ROUTES.USER.PROFILE}
                className={styles.dropdownItem}
                onClick={() => setIsDropdownOpen(false)}
              >
                <User size={18} />
                <span>View Profile</span>
              </Link>
              <Link
                to={ROUTES.USER.SETTINGS}
                className={styles.dropdownItem}
                onClick={() => setIsDropdownOpen(false)}
              >
                <SlidersHorizontal size={18} />
                <span>App Settings</span>
              </Link>
              <Link
                to={ROUTES.COMPANIES.ROOT}
                className={styles.dropdownItem}
                onClick={() => setIsDropdownOpen(false)}
              >
                <Building2 size={18} />
                <span>Companies</span>
              </Link>
              {availableCompanies.length > 1 && (
                <div className={styles.dropdownDivider} />
              )}
              {availableCompanies.length > 1 && (
                <div className={styles.dropdownSection}>
                  <div className={styles.dropdownSectionLabel}>
                    Switch Company
                  </div>
                  {availableCompanies.map((company) => (
                    <button
                      key={company.id}
                      className={`${styles.dropdownItem} ${styles.dropdownItemWithCheck} ${
                        activeCompanyId === company.id
                          ? styles.dropdownItemActive
                          : ''
                      }`}
                      onClick={() => void handleSwitchCompany(company.id)}
                    >
                      <div className={styles.dropdownItemContent}>
                        {companyLogoUrls[company.id] ? (
                          <img
                            src={companyLogoUrls[company.id] ?? undefined}
                            alt=""
                            aria-hidden
                            className={styles.companyLogo}
                          />
                        ) : (
                          <Building2 size={18} />
                        )}
                        <span>{company.name}</span>
                        {company.owned && (
                          <span className={styles.ownerBadge}>Owner</span>
                        )}
                      </div>
                      {activeCompanyId === company.id && (
                        <Check size={16} className={styles.checkIcon} />
                      )}
                    </button>
                  ))}
                </div>
              )}
              <div className={styles.dropdownDivider} />
              <button
                className={styles.dropdownItem}
                onClick={() => void handleLogout()}
              >
                <LogOut size={18} />
                <span>Logout</span>
              </button>
            </div>
          )}
        </div>
      </header>
    </>
  );
}

export default Header;
